import { money, soFar } from '../format.js';
import Peek, { useMeetingContext } from '../Peek.jsx';
import { Ink, inkAt } from '../AnchoredInk.jsx';
import {
  AssetEditor, SpendingEditor, IncomeEditor, PeopleEditor, ToldEditor, GoalLeadEditor, PageGoalsEditor,
  useMeetingEdits, MoneyInput, AddButton, DropButton,
} from '../Editable.jsx';

// Where the meeting opens: the goal sheet advisors already know.
//
// It is the one page every advisor presents, so it comes first, and what the
// household said it wants is set into it rather than given a page of its own
// (Amanda Cook, 2026-10-02). The first heading is the goal that fits the
// household's stage of life, not the same line for everyone, and an advisor
// can change it by clicking it.
//
// The older report's sheet, which is the one part of the Word assessment
// clients and advisors actually read: same two headings, same numbering, same
// "+Tax+COLA", the shortfall in red at the foot, and section 2 read from one
// named year so the page cannot disagree with itself. When there is a bridge
// (retired before every benefit has started) the advisor can switch which
// years section 2 reads from, and the choice is kept with the assessment.
//
// Set in the redesign's two columns and colours. Every figure opens onto the
// questionnaire rows behind it. Which years section 2 reads from is chosen
// beside the slide rather than on it, because the choice is the advisor's and
// the page is the client's.

const ROMAN = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x', 'xi', 'xii', 'xiii', 'xiv', 'xv', 'xvi'];
const QUALIFIER = '+Tax+COLA';

function Line({ n, label, value, qualifier, variant, peek }) {
  const figure = (
    <>
      {value}
      {qualifier ? <small>{qualifier}</small> : null}
    </>
  );
  return (
    <div className={`conv-gs-row${variant ? ` conv-gs-${variant}` : ''}`} {...inkAt(`gs.${label}`)}>
      {/* A mark drawn over this row, where the sheet is the brief's first page. */}
      <Ink at={`gs.${label}`} />
      <span className="conv-gs-num">{n}</span>
      <span>{label}</span>
      <span className="conv-gs-value">
        {peek ? <Peek title={peek.title} editor={peek.editor}>{figure}</Peek> : figure}
      </span>
    </div>
  );
}

const orNot = v => (v === null || v === undefined ? 'Not recorded' : money(v));

const FALLBACK = { other: 'Other income', pension: 'Pension income' };
const sameLine = (a, b) => a.key === b.key && a.index === b.index;

// The income assumptions while the advisor is preparing the sheet.
//
// An advisor getting ready for a meeting works on this list the way they
// always have on the paper one: retitle a line, change its amount, add one
// ("Real estate income") (Jimmy, 2026-10-03). So with edit mode on, every
// line that is a row of its own is a title and an amount to type into, and
// there is a link under them to add another. Each is a row of the
// questionnaire's own income tables, so what is typed here is what planning
// is sent, and the total beneath is always the sum of what is listed.
//
// A line added here can be taken away again. A line that was already in the
// questionnaire is not removed from this page, only changed.
function IncomeLinesEditor({ own, tail }) {
  const e = useMeetingEdits();
  // Rows added here and not yet filled in: nothing lists them until they
  // carry something, so they are kept on the page by hand.
  const drafts = e.shown('income.other')
    .filter(([r, index]) => e.isDraft(r) && !own.some(s => sameLine(s, { key: 'other', index })))
    .map(([, index]) => ({ key: 'other', index }));
  const lines = [
    ...own.filter(s => s.key === 'other'),
    ...drafts,
    ...own.filter(s => s.key !== 'other'),
  ];

  return (
    <>
      {lines.map((s, n) => {
        const path = `income.${s.key}`;
        const row = e.rows(path)[s.index] || {};
        const title = String(row.description || '').trim() || FALLBACK[s.key];
        return (
          <div className="conv-gs-row is-editable" key={`${s.key}:${row._uid || s.index}`}>
            <span className="conv-gs-num">{`${ROMAN[n]}.`}</span>
            <span className="conv-gs-label">
              <input
                type="text"
                className="conv-text"
                aria-label={`Income line ${n + 1} title`}
                value={row.description ?? ''}
                placeholder={s.key === 'other' ? 'Real estate income, for example' : FALLBACK[s.key]}
                onChange={ev => e.setRow(path, s.index, { description: ev.target.value })}
              />
            </span>
            <span className="conv-gs-value conv-gs-edit">
              <MoneyInput value={row.annualAmount} label={`${title} annual amount`} onChange={v => e.setRow(path, s.index, { annualAmount: v })} />
              {e.isDraft(row)
                ? <DropButton label={`Remove ${title}`} onClick={() => e.dropDraft(path, row)} />
                : <span className="conv-gs-nodrop" aria-hidden="true" />}
            </span>
          </div>
        );
      })}
      <div className="conv-gs-add no-print">
        <AddButton onClick={() => e.addRow('income.other', { description: '', annualAmount: '' })}>Add an income line</AddButton>
      </div>
      {tail(lines.length)}
    </>
  );
}

// What the household said it wants, in its own words. However many lines
// there are, they sit in one band under the title; with none there is no band,
// and Adjust shows where to add them.
function Told({ told, shown = true, fromMeeting = false, locked = false }) {
  const { adjustable, adjusting, offerEmpty, setMeeting } = useMeetingContext();
  // Once the page has its own list (Claude's goals reviewed, or edits made
  // here), that list is what is edited; the questionnaire is left as written.
  const editor = () => (fromMeeting ? <PageGoalsEditor /> : <ToldEditor />);
  const note = fromMeeting
    ? "One goal per line, kept with this assessment. The questionnaire's goals are unchanged."
    : "One thought per line, kept in the questionnaire's Goals.";
  // The advisor can leave the goals section off the page altogether (Jimmy,
  // 2026-10-20), and bring it back. The goals themselves stay in the
  // questionnaire; only the section goes. A locked section (brief/briefLocks.js)
  // stays.
  const remove = adjustable && !locked ? (
    <button type="button" className="conv-told-x no-print" onClick={() => setMeeting('goalsSection', false)} aria-label="Remove the Goals section" title="Remove the Goals section">×</button>
  ) : null;
  if (!shown) {
    return adjustable ? (
      <div className="conv-goal-told-off no-print">
        <button type="button" className="conv-told-back" onClick={() => setMeeting('goalsSection', true)}>Add the Goals section back</button>
      </div>
    ) : null;
  }
  if (!told?.length) {
    return adjustable && (adjusting || offerEmpty) ? (
      <div className="conv-goal-told is-empty no-print">
        {remove}
        <Peek title="Your goals" note={note} editor={editor}>Add what they told you matters to them</Peek>
      </div>
    ) : null;
  }
  return (
    <div className="conv-goal-told" {...inkAt('gs.told')}>
      <Ink at="gs.told" />
      {remove}
      <span className="conv-eyebrow">Goals</span>
      <Peek className="conv-goal-told-body" title="Your goals" note={note} editor={editor}>
        <ul data-count={Math.min(told.length, 3)}>{told.map((t, i) => <li key={i}>{t}</li>)}</ul>
      </Peek>
    </div>
  );
}

function Head({ name, told, toldShown, toldFromMeeting, toldLocked, asOf }) {
  return (
    <>
      <div className="conv-eyebrow" {...inkAt('gs.eyebrow')}>
        <Ink at="gs.eyebrow" />
        {`Financial assessment${name ? ` for ${name}` : ''}${asOf ? ` · ${asOf}` : ''}`}
      </div>
      <h1 className="conv-goal-title" {...inkAt('gs.title')}><Ink at="gs.title" />Financial planning goals and concerns</h1>
      <Told told={told} shown={toldShown || toldLocked} fromMeeting={toldFromMeeting} locked={toldLocked} />
    </>
  );
}

export default function GoalSheet({ sheet, lead = '', told = [], toldShown = true, toldFromMeeting = false, toldLocked = false, name = '', asOf = '' }) {
  const { people, adjustable, adjusting } = useMeetingContext();
  const editing = Boolean(adjustable && adjusting);
  if (!sheet) {
    return (
      <>
        <Head name={name} told={told} toldShown={toldShown} toldFromMeeting={toldFromMeeting} toldLocked={toldLocked} asOf={asOf} />
        <div className="conv-board-waiting conv-gs-waiting">
          <strong>There is not enough recorded yet to set out your goals and concerns.</strong>
          <span>It needs an age for each person, so the years ahead can be counted.</span>
        </div>
      </>
    );
  }
  const { liquid, income } = sheet;

  const years = `${income.yearsAway} ${income.yearsAway === 1 ? 'year' : 'years'}`;
  const atRetirement = income.period === 'unresolved'
    ? 'Income required today'
    : income.alreadyThere
      ? 'Income required now that you are retired'
      : income.period === 'bridge'
        ? `Income required at retirement, before Social Security (${years})`
        : `Income required at retirement (${years})`;

  const timing = { title: 'Retirement timing', editor: () => <PeopleEditor people={people} /> };
  const spending = { title: 'Annual spending', editor: () => <SpendingEditor /> };

  // Under "Income assumptions": first the lines that are rows of their own (a
  // pension, a rental), then the ones that stand for a whole table, the total
  // and what is left over. `tail` draws everything after the first group,
  // numbered on from wherever that group ended, which is further along while
  // a line is being added.
  const own = income.sources.filter(s => s.index !== undefined);
  const whole = income.sources.filter(s => s.index === undefined);
  const tail = from => (
    <>
      {whole.map((s, i) => (
        <Line key={s.key} n={`${ROMAN[from + i]}.`} label={s.label} value={orNot(s.amount)}
          peek={{ title: s.label, editor: () => <IncomeEditor only={[s.key]} /> }} />
      ))}
      <Line n={`${ROMAN[from + whole.length]}.`} label="Total income" value={income.waitingOnSocialSecurity ? soFar(income) : orNot(income.totalIncome)} variant="total"
        peek={{ title: 'Income', editor: () => <IncomeEditor /> }} />
      <Line n={`${ROMAN[from + whole.length + 1]}.`}
        label={income.shortfall === null ? 'Surplus or shortfall' : income.shortfall > 0 ? 'Shortfall needed from investments' : 'Surplus, before tax and inflation'}
        value={income.waitingOnSocialSecurity ? 'Open until Social Security is added' : income.shortfall === null ? 'Not recorded' : money(income.shortfall > 0 ? income.shortfall : income.surplus)}
        qualifier={income.shortfall === null ? '' : QUALIFIER}
        variant={income.shortfall > 0 ? 'short' : 'result'} />
    </>
  );

  return (
    <>
      <Head name={name} told={told} toldShown={toldShown} toldFromMeeting={toldFromMeeting} toldLocked={toldLocked} asOf={asOf} />

      <div className="conv-gs-sections">
        <section className="conv-gs-section">
          <div className="conv-gs-heading" {...inkAt('gs.s1')}>
            <Ink at="gs.s1" />
            <span>01</span>
            <h2>
              <Peek title="First goal" saves="assessment" note="Kept with this assessment." editor={() => <GoalLeadEditor />}>
                {lead || 'Preservation and conservative growth of principal'}
              </Peek>
            </h2>
          </div>
          <div className="conv-gs-group">
            <h3 {...inkAt('gs.liquid')}><Ink at="gs.liquid" />a. Liquid investments</h3>
            <Line n="i." label="Tax-deferred investments" value={orNot(liquid.taxDeferred)}
              peek={{ title: 'Tax-deferred accounts', editor: () => <AssetEditor only={['taxDeferred']} /> }} />
            <Line n="ii." label={liquid.taxFreeLabel || 'Tax-free Roth investments'} value={orNot(liquid.taxFree)}
              peek={{ title: 'Tax-free accounts', editor: () => <AssetEditor only={['roth', 'hsa']} /> }} />
            <Line n="iii." label="Taxable investments" value={orNot(liquid.taxable)}
              peek={{ title: 'Taxable accounts', editor: () => <AssetEditor only={['taxable', 'cashCd']} /> }} />
            <Line n="iv." label="Total liquid investments" value={orNot(liquid.total)} variant="total"
              peek={{ title: 'Liquid investments', editor: () => <AssetEditor only={['taxDeferred', 'roth', 'hsa', 'taxable', 'cashCd']} /> }} />
          </div>
        </section>

        <section className="conv-gs-section">
          <div className="conv-gs-heading" {...inkAt('gs.s2')}>
            <Ink at="gs.s2" />
            <span>02</span>
            <h2>Retirement income and cash flow strategy</h2>
          </div>
          <div className="conv-gs-group">
            <Line n="a." label="Income required today" value={orNot(income.requiredToday)}
              qualifier={income.requiredToday !== null ? QUALIFIER : ''} peek={spending} />
            <Line n="b." label={atRetirement} value={orNot(income.requiredAtRetirement)}
              qualifier={income.requiredAtRetirement !== null ? QUALIFIER : ''} peek={timing} />
            <h3 {...inkAt('gs.assumptions')}><Ink at="gs.assumptions" />c. Income assumptions</h3>
            {editing ? <IncomeLinesEditor own={own} tail={tail} /> : (
              <>
                {own.map((s, i) => (
                  <Line key={`${s.key}:${s.index}`} n={`${ROMAN[i]}.`} label={s.label} value={orNot(s.amount)}
                    peek={{ title: s.label, editor: () => <IncomeEditor only={[s.key]} /> }} />
                ))}
                {tail(own.length)}
              </>
            )}
          </div>
        </section>
      </div>

      {income.waitingOnSocialSecurity ? (
        <p className="conv-caption conv-gs-foot" {...inkAt('gs.note.ss')}>
          <Ink at="gs.note.ss" />
          No Social Security figure is recorded, so the total and the shortfall are left open rather than read as if
          the benefit were zero. Add the expected benefit and both fill in.
        </p>
      ) : income.totalIncome === null && income.period !== 'unresolved' ? (
        <p className="conv-caption conv-gs-foot" {...inkAt('gs.note.amount')}>
          <Ink at="gs.note.amount" />
          An income source was started without an amount, so the total and the shortfall are left open until it is
          filled in.
        </p>
      ) : income.socialSecurityStartAssumed ? (
        <p className="conv-caption conv-gs-foot" {...inkAt('gs.note.start')}>
          <Ink at="gs.note.start" />
          No Social Security starting age is recorded, so the benefit is counted from today. When it actually starts
          changes the years it covers.
        </p>
      ) : null}
      {(
        income.period === 'unresolved' ? (
          <p className="conv-caption conv-gs-foot" {...inkAt('gs.note.read')}>
            <Ink at="gs.note.read" />
            {`Section 2 is read from ${income.atYear}, this year, because the questionnaire does not say when `}
            {income.unresolvedRetirement.length === 1 ? 'this household retires' : 'both people retire'}
            . Add a retirement age or date, or a date of birth to measure the date against, and this section reads
            from the year it belongs to.
          </p>
        ) : income.period === 'bridge' && income.bridge ? (
          <p className="conv-caption conv-gs-foot" {...inkAt('gs.note.read')}>
            <Ink at="gs.note.read" />
            {`Section 2 is read from ${income.atYear}, the first year of retirement, before every Social Security payment has started. That gap runs ${income.bridge.years} ${income.bridge.years === 1 ? 'year' : 'years'}, to ${income.bridge.toYear}, and costs ${money(income.bridge.totalGap)} in total. The shortfall is one year of it.`}
          </p>
        ) : !income.alreadyThere && income.waitingOnSocialSecurity ? (
          // With no benefit recorded, nothing can be said about when it starts.
          <p className="conv-caption conv-gs-foot" {...inkAt('gs.note.read')}>
            <Ink at="gs.note.read" />
            {`Section 2 is read from ${income.atYear}, the first year of retirement. When Social Security starts is not known until it is recorded.`}
          </p>
        ) : !income.alreadyThere ? (
          <p className="conv-caption conv-gs-foot" {...inkAt('gs.note.read')}>
            <Ink at="gs.note.read" />
            {`Section 2 is read from ${income.atYear}, the first year you are both retired and every Social Security payment has started. The shortfall is the subtraction of the two figures above it, at that one point in time.`}
          </p>
        ) : null
      )}
      <p className="conv-caption conv-gs-foot" {...inkAt('gs.note.qualifier')}>
        <Ink at="gs.note.qualifier" />
        {`${QUALIFIER} means before tax and before inflation: the figure is what it costs to live, not what has to be withdrawn to pay for it once tax is taken and prices have risen.`}
      </p>
    </>
  );
}
